import React, { ComponentType, FC, useEffect, useState } from 'react';
import { world } from '../../languages';
import { getCountries } from '../../api/geo/get-countries';
import cn from '../../utils/classnames';
import Dropdown from '../dropdown/dropdown';
import Flag from './flag';
import './country-dropdown.scss';

export type CountryItem = {
    alpha2: string;
    id: string;
    name: string;
};

export type CountryDropdownProps = {
    noDefaultCountry: boolean;
    onChange: (e: CountryItem) => void;
    translatedList: CountryItem[];
    value: string;
    disabledCountries?: string[];
};

const Component: FC<CountryDropdownProps> = ({
    onChange,
    value = world,
    translatedList = [],
    noDefaultCountry,
    disabledCountries = []
}) => {
    const filteredCountries = translatedList;
    const [countries, changeCountries] = useState(filteredCountries?.slice());

    useEffect(() => {
        disabledCountries.forEach((dc) => {
            const i = document.querySelector(`.${dc}.dropdown-item`);

            if (i) {
                i.classList.add('disabled');

                i.addEventListener('click', (e) => {
                    e.stopPropagation();
                    e.preventDefault();
                });
            }
        });
    }, []);

    function defineCountry(): CountryItem {
        const filtered = filteredCountries.filter((c: CountryItem) => c.alpha2 === `${value}`.toLowerCase())[0];

        if (filtered) {
            return filtered;
        }

        // Our bookkeeper requested possibility not to set default country for cabinet invoice form
        if (noDefaultCountry) {
            return {} as CountryItem;
        }

        return filteredCountries.filter((c: CountryItem) => c.alpha2 === 'de')[0];
    }

    const country = defineCountry();

    return (
        <Dropdown.Button
            key='licence'
            id='country-dropdown'
            className='country-dropdown'
            name='country-dropdown'
            variant='default'
            autoClose='select-outside'
            title={
                <>
                    <Flag code={country?.alpha2} />
                    {country?.name}
                </>
            }
        >
            <div className='c-search-wrapper'>
                <input
                    className='form-control'
                    onChange={(e) => {
                        const newList = filteredCountries.filter(
                            (f: { name: string }) => f.name.toLowerCase().indexOf(e.target.value.toLowerCase()) >= 0
                        );

                        changeCountries(newList);
                    }}
                />
            </div>
            {countries.map((c: CountryItem) => {
                const code = c.alpha2.toUpperCase();
                const isDisabled = disabledCountries.indexOf(code) >= 0;
                const name = isDisabled ? `${c.name} - nicht Verfügbar` : c.name;

                return (
                    <Dropdown.Item
                        key={c.id}
                        selected={country?.alpha2?.toUpperCase() === code}
                        className={cn('dropdown-item', { restricted: isDisabled })}
                        onClick={() => {
                            if (!isDisabled) {
                                onChange(c);
                            }
                        }}
                    >
                        <Flag code={c.alpha2} />
                        <span className='ellipsis'>{name}</span>
                    </Dropdown.Item>
                );
            })}
        </Dropdown.Button>
    );
};

const withAPIData = <T extends CountryDropdownProps>(WrappedComponent: ComponentType<T>) => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    return (props: any) => {
        const [countries, setCountries] = useState([] as CountryItem[]);

        const processCountries = (mapping: { [key: string]: string }) => {
            const list = Object.keys(mapping);

            return list.map((code, index) => ({
                id: index,
                name: mapping[code],
                alpha2: code.toLowerCase()
            }));
        };

        useEffect(() => {
            getCountries().then((res) => {
                // @ts-ignore
                setCountries(processCountries(res));
            });
        }, []);

        if (!countries.length) {
            return null;
        }

        const extendedProps = {
            ...props,
            translatedList: countries
        };

        return <WrappedComponent {...extendedProps} />;
    };
};

const CountryDropdown = withAPIData(Component);

export default CountryDropdown;
